import React, {ReactNode, useEffect, useState} from "react";
import {Box, Button, ButtonBase, Stack, Typography} from "@mui/material";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {RecruitmentStats, RecruitmentStatsRestControllerApiFactory} from "arteco-api-client-ts";
import {useNavigate} from "react-router-dom";
import {useApiManager} from "../../../components/utils/api";
import {EmptyState, Page, PageHeader, Panel, PersonAvatar, SkeletonRows, SoftChip, StatGrid, StatTile} from "../../../components/kit";

/** Una fila pulsable de las tarjetas de la portada: la persona a la izquierda y el dato a la derecha. */
const Row = ({name, caption, right, onClick}: { name?: string, caption?: ReactNode, right?: ReactNode, onClick: () => void }) => (
	<ButtonBase onClick={onClick} sx={{
		width: "100%", justifyContent: "space-between", gap: 1.5, py: 1, px: 1, mx: -1, borderRadius: 2, textAlign: "left",
		"&:hover": {bgcolor: "action.hover"},
	}}>
		<Box sx={{minWidth: 0, display: "flex", alignItems: "center", gap: 1.5}}>
			<PersonAvatar name={name} size={30}/>
			<Box sx={{minWidth: 0}}>
				<Typography variant="body2" fontWeight={600} noWrap>{name}</Typography>
				{caption && <Typography variant="caption" color="text.secondary" component="div" noWrap>{caption}</Typography>}
			</Box>
		</Box>
		{right && <Box sx={{flexShrink: 0}}>{right}</Box>}
	</ButtonBase>
);

/**
 * Portada de selección (#13893): candidatos con puntuación máxima pendientes de evaluar y personas en
 * procesos abiertos con su estado. Cada fila lleva a la ficha del candidato o a la candidatura.
 */
export const AdminRecruitmentPage = () => {
	const navigate = useNavigate();
	const apiManager = useApiManager();
	const [stats, setStats] = useState<RecruitmentStats>();

	useEffect(() => {
		apiManager.execute(apiManager.factory(RecruitmentStatsRestControllerApiFactory).getRecruitmentStats())
			.then(setStats).catch(() => setStats({}));
	}, []);

	const fresh = stats?.newCandidates || [];
	const inProgress = stats?.progressCandidates || [];

	return (
		<Page>
			<PageHeader title="Selección" subtitle="Candidatos por evaluar y procesos abiertos."
						actions={<>
							<Button variant="outlined" startIcon={<WorkOutlineRoundedIcon/>} onClick={() => navigate("/app/admin/recruitment/recruitment")}>
								Procesos
							</Button>
							<Button variant="contained" startIcon={<AddRoundedIcon/>} onClick={() => navigate("/app/admin/recruitment/candidate/new")}>
								Nuevo candidato
							</Button>
						</>}/>

			<StatGrid>
				<StatTile icon={<PersonAddAlt1RoundedIcon fontSize="small"/>} tone={fresh.length ? "info" : "neutral"}
						  label="Candidatos por evaluar" value={fresh.length} loading={!stats}
						  onClick={() => navigate("/app/admin/recruitment/candidate")}/>
				<StatTile icon={<WorkOutlineRoundedIcon fontSize="small"/>} tone={inProgress.length ? "warning" : "neutral"}
						  label="Personas en procesos abiertos" value={inProgress.length} loading={!stats}
						  onClick={() => navigate("/app/admin/recruitment/recruitment")}/>
			</StatGrid>

			<Box sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))"}, gap: 3, alignItems: "start"}}>
				<Panel title="Candidatos por evaluar" subtitle="Con puntuación 100, sin evaluar todavía">
					{!stats ? <SkeletonRows rows={3} height={36}/> : fresh.length === 0 ? (
						<EmptyState title="No hay candidatos nuevos"/>
					) : (
						<Stack>
							{fresh.map((c, i) => (
								<Row key={i} name={c.candidateName} onClick={() => navigate("/app/admin/recruitment/candidate/" + c.candidateId)}/>
							))}
						</Stack>
					)}
				</Panel>

				<Panel title="En procesos abiertos" subtitle="Personas en procesos con estado no finalizado">
					{!stats ? <SkeletonRows rows={3} height={36}/> : inProgress.length === 0 ? (
						<EmptyState title="No hay candidatos en procesos activos"/>
					) : (
						<Stack>
							{inProgress.map((c, i) => (
								<Row key={i} name={c.candidateName} caption={c.recruitmentName}
									 right={c.state?.name ? <SoftChip tone="info" label={c.state.name}/> : undefined}
									 onClick={() => navigate("/app/admin/recruitment/candidate/undefined/application/" + c.applicationId)}/>
							))}
						</Stack>
					)}
				</Panel>
			</Box>
		</Page>
	);
};
